import { useMemo, useState } from 'react';
import { useData } from 'shell/dataContext';
import { AD_REVIEW_SURVEY_OPTION, SURVEY_OPTIONS, SurveyValues } from '../constants/surveyOptions';
import { getProjects, importProjects } from '../data/utils/api.service';
import { useDebounce } from '../data/hooks/useDebounce';
import { useEvent } from '../../common/hooks/useEvent.hook';
import { importProjectModalEvents } from '../../../utils/events/importProject.events';
import { IMPORT_PROJECT, NOT_IMPORTABLE_REASONS_AS_EXCEPTIONS, PROJECT_ID, PROJECT_TYPE } from '../constants/constants';
import { useError } from './useError.hook';
import { useCheckbox } from './useCheckbox.hook';
import { useModal } from './useModal.hook';

import { ImportProjectResponse, NotImportableReasonsAsExceptionsObject, ResponseObject } from '../data/models';
import type { IImportProjectProps } from './ImportProject';

export const useImportProjectHook = (props: IImportProjectProps) => {
    const { showModal, onClose, translations } = props;

    const {
        errors,
        handleSetError,
        handleGetError,
        projectIdError,
        projectTypeError,
        importProjectError,
        notImportableReasonsAsExceptions,
    } = useError();
    const { setCheckedItems, checkedItems, handleCheckItems, handleUncheckItems } = useCheckbox();
    const {
        showImportProjectModal,
        isLoadImportProjectModal,
        handleShowModal,
        setShowImportProjectModal,
        setIsLoadImportProjectModal,
    } = useModal(!!showModal);

    const [importProject, setImportProject] = useState<Array<ResponseObject> | null>(null);
    const [projectType, setProjectType] = useState<(typeof SURVEY_OPTIONS)[number] | null>(null);
    const [projectId, setProjectId] = useState<string | null>(null);
    const [isLoadImportingProject, setIsLoadImportingProject] = useState<boolean>(false);
    const [importProjectResponse, setImportProjectResponse] = useState<{
        projectId?: ImportProjectResponse['projectId'];
        cultures?: ImportProjectResponse['cultures'];
        isMultiMarket?: ImportProjectResponse['isMultiMarket'];
    } | null>(null);

    const isImportDisabled = useMemo(
        () => !checkedItems?.length || isLoadImportingProject,
        [checkedItems, isLoadImportingProject]
    );

    const { isHosted, redirectToUrl, userData } = useData() ?? {};

    const handleShowDropdownOptions = () => {
        if (importProjectResponse?.isMultiMarket) {
            return SURVEY_OPTIONS.filter(({ value }) => value === SurveyValues.CUSTOM_SURVEY);
        }

        if (userData?.userDetailsTraits?.traits?.IsAdReviewCreateOnEnabled) {
            return [...SURVEY_OPTIONS, AD_REVIEW_SURVEY_OPTION];
        }

        return SURVEY_OPTIONS;
    };

    const handleResetSearch = () => {
        setProjectType(null);
        setProjectId(null);
        setCheckedItems(null);
        setImportProject(null);
        setIsLoadImportingProject(false);
        handleSetError(null);
        setImportProjectResponse(null);
    };

    const handleHideModal = () => {
        if (!isLoadImportProjectModal) {
            document.body.style.overflow = '';
            setShowImportProjectModal(false);
            onClose?.();
            handleResetSearch();
        }
    };

    const handleNavigate = (
        selectedProjectType: SurveyValues,
        selectedProjectId: ResponseObject['projectId'],
        cultureIds: ResponseObject['cultureIds']
    ) => {
        const { CREATE_CUSTOM_SURVEY_URL, LEGACY_SHELL_URL } = window.ts.globals;

        const cultures = cultureIds.join(',');

        let redirectUrl: string;

        switch (selectedProjectType) {
            case SurveyValues.CUSTOM_SURVEY:
                redirectUrl = `${CREATE_CUSTOM_SURVEY_URL}?projectId=${selectedProjectId}&supportedCultures=${cultures}`;
                break;

            case SurveyValues.MAXDIFF:
                redirectUrl = `${CREATE_CUSTOM_SURVEY_URL}?maxdiff&projectId=${selectedProjectId}&supportedCultures=${cultures}`;
                break;

            default:
                redirectUrl = `${LEGACY_SHELL_URL}/create?templateId=${selectedProjectType}&projectId=${selectedProjectId}&supportedCultures=${cultures}`;
        }

        if (!isHosted) {
            window.location.href = redirectUrl;
            return;
        }

        handleHideModal();
        const url = new URL(redirectUrl);

        redirectToUrl?.(`${url.pathname}${url.search}`);
    };

    const handleProjectChange = (value: string) => {
        setProjectId(value ?? null);
        handleSetError({
            input: PROJECT_ID,
            value: !value,
            message: translations?.TI2020_ContentTranslations_Header_ImportProject_ProjectIDCanNotBeEmpty,
        });
    };

    const handleSetProjectType = (value: (typeof SURVEY_OPTIONS)[number]) => {
        setProjectType(value);
        handleSetError({ input: PROJECT_TYPE, value: false, message: '' });
    };

    const handleSearchProject = () => {
        if (!projectId || !projectType?.value) {
            handleSetError({
                input: PROJECT_ID,
                value: !projectId,
                message: translations?.TI2020_ContentTranslations_Header_ImportProject_ProjectIDCanNotBeEmpty,
            });
            handleSetError({
                input: PROJECT_TYPE,
                value: !projectType?.value,
                message: translations?.TI2020_ContentTranslations_Header_ImportProject_ProjectTypeCanNotBeEmpty,
            });
            return;
        }

        handleSetError(null);
        setIsLoadImportProjectModal(true);
        getProjects(
            projectId,
            response => {
                setIsLoadImportProjectModal(false);
                if (response.isImportable) {
                    setImportProject(response.data);

                    if (response.isImportedInTsiButNotInTS || response.isMultiMarket) {
                        const importResponse: Partial<ImportProjectResponse> = {};

                        if (response.isImportedInTsiButNotInTS) {
                            importResponse.projectId = response.projectId;
                            importResponse.cultures = response.cultures;
                        }

                        if (response.isMultiMarket) {
                            importResponse.isMultiMarket = true;
                        }

                        setImportProjectResponse(importResponse);
                    }

                    if (response.data?.length <= 1) {
                        handleCheckItems(response.data[0].externalProjectId);
                    }
                } else {
                    handleSetError({
                        input: NOT_IMPORTABLE_REASONS_AS_EXCEPTIONS,
                        value: true,
                        message: response.notImportableReasonsAsExceptions.map(
                            ({ message }: NotImportableReasonsAsExceptionsObject) => message
                        ),
                    });
                }
            },
            () => {
                setIsLoadImportProjectModal(false);
                handleSetError({
                    input: PROJECT_ID,
                    value: true,
                    message:
                        translations?.TI2020_ContentTranslations_Header_ImportProject_AnErrorOccurredDuringSearching,
                });
            }
        );
    };

    const handleImportProjects = () => {
        setIsLoadImportingProject(true);
        handleSetError(null);

        if (
            importProjectResponse &&
            projectType?.value &&
            importProjectResponse.projectId &&
            importProjectResponse.cultures
        ) {
            handleNavigate(projectType?.value, importProjectResponse.projectId, importProjectResponse.cultures);
            return;
        }

        if (Boolean(checkedItems?.length) && checkedItems?.length! && projectType?.value && !importProjectResponse) {
            importProjects(
                checkedItems,
                {
                    checkedItems,
                    projectType: projectType.value,
                },
                response => {
                    handleResetSearch();
                    handleNavigate(
                        projectType.value,
                        response.importedProjects?.[0]?.projectId,
                        response.importedProjects?.[0]?.surveys?.map(
                            ({ cultures }: { cultures: ResponseObject['cultureIds'] }) => cultures?.[0]
                        )
                    );
                },
                () => {
                    setIsLoadImportingProject(false);
                    handleSetError({
                        input: IMPORT_PROJECT,
                        value: true,
                        message:
                            translations?.TI2020_ContentTranslations_Header_ImportProject_AnErrorOccurredDuringImporting,
                    });
                }
            );
        }
    };

    const debouncedHandleProjectChange = useDebounce(handleProjectChange, 500);

    useEvent({ name: importProjectModalEvents.SHOW_IMPORT_PROJECT, handler: handleShowModal });
    useEvent({ name: importProjectModalEvents.HIDE_IMPORT_PROJECT, handler: handleHideModal });

    return {
        showImportProjectModal,
        isLoadImportProjectModal,
        projectType,
        projectId,
        checkedItems,
        importProject,
        notImportableReasonsAsExceptions,
        isLoadImportingProject,
        isImportDisabled,
        importProjectResponse,
        errors,
        projectIdError,
        projectTypeError,
        importProjectError,
        handleShowDropdownOptions,
        handleShowModal,
        handleResetSearch,
        handleHideModal,
        handleProjectChange,
        handleCheckItems,
        handleUncheckItems,
        handleSetError,
        handleGetError,
        handleSearchProject,
        handleImportProjects,
        debouncedHandleProjectChange,
        handleSetProjectType,
    };
};
